/**
 * Step 4: Create fan for a single tunnel
 *
 * Flow (matches old Backbone get_create_fan):
 * 1. Get fan and next section info
 * 2. Rate the fan FIRST (get_fan)
 * 3. Create fan section
 * 4. Create fan with rated data
 * 5. Update section length based on fan clearances
 * 6. Validate tunnel
 */
import { IBotData, ISection, DEFAULT_COEFFICIENTS } from '../../../models';
import { dispatchMutation, dispatchQuery } from '../../../utils/dispatch.utils';
import { ahuApi } from '../../../services';
import { createFanWithRatedData, getFanAndNextSection } from './fan';
import { roundBasedOnArguments } from '../designAHU.utils';

export async function createFanForTunnel(
  _botTunnel: any,
  tunnelId: number,
  validSections: any[],
  botData: IBotData,
  casingInsulationId: number | null,
  casingInnerlinerId: number | null,
  floorInnerlinerId: number | null,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<{
  tunnelFan: any | null;
  fanError?: { sectionId: string; sectionType: 'fan'; error: string };
}> {
  // Get fan and next section info (matches old Backbone getFanAndNextSection)
  const { fan, nextSection, first, last } = getFanAndNextSection(validSections);
  const fanRatingData = fan?._ratingData;
  // If no fan in this tunnel, return null
  if (!fan || !fanRatingData) {
    return { tunnelFan: null };
  }
  // ========================================================================
  // STEP 1: Rate the fan FIRST (matches old Backbone: await self.get_fan(fan.toJSON()))
  // ========================================================================
  onProgress?.(DEFAULT_COEFFICIENTS.fan_rate); // fan_rate coefficient
  // Check for errors and store on section instead of throwing
  if (fanRatingData.error) {
    // Return null to indicate fan creation failed
    return {
      tunnelFan: null,
      fanError: {
        sectionId: fan.id,
        sectionType: 'fan',
        error: fanRatingData.error,
      },
    };
  }
  // ========================================================================
  // STEP 2: Create fan section (matches old Backbone: create section API call)
  // ========================================================================
  const fanSectionData: ISection = await dispatchMutation(
    ahuApi.endpoints.createSection,
    {
      body: {
        length: 12,
        sitting_on_id: null,
        split: false,
        tag: '',
        tunnel_id: tunnelId,
        //order_rate: orderRate,
        casing_insulation_id: casingInsulationId,
        casing_innerliner_id: casingInnerlinerId,
        floor_innerliner_id: floorInnerlinerId,
      },
    },
    getOptions(),
  );
  onProgress?.(DEFAULT_COEFFICIENTS.create_section); // create_section coefficient
  // ========================================================================
  // STEP 3: Create fan with rated data (matches old Backbone: await self.create_fan)
  // ========================================================================
  const voltageId = botData.voltage_id || 1;
  const createdFan = await createFanWithRatedData(
    fanSectionData.id,
    fanRatingData,
    voltageId,
    getOptions,
  );
  
  onProgress?.(DEFAULT_COEFFICIENTS.fan_create); // fan_create coefficient
  // ========================================================================
  // STEP 4: Update fan section length based on clearances (matches old Backbone)
  // ========================================================================
  let updatedSection = fanSectionData;
  if (nextSection && nextSection.type) {
    try {
      const section: ISection = await dispatchQuery(
        ahuApi.endpoints.getSection,
        fanSectionData.id,
        getOptions(),
      );
      fan.model = section;
      // Calculate length with fan clearances
      let length = +section.required_length || 12;
      if (createdFan.min_clear_downs) {
        length = +section.required_length + (createdFan.min_clear_downs || 0);
      }
      // Adjust for insulation thickness
      if (!first) length -= section.casing_insulation_thick || 0;
      if (!last) length -= section.casing_insulation_thick || 0;
      // Auto-adjust if next section is plenum
      const isPlenum = nextSection.type === 'plenum';
      updatedSection = await dispatchMutation(
        ahuApi.endpoints.updateSection,
        {
          id: fanSectionData.id,
          body: {
            auto_adjust: isPlenum,
            length: roundBasedOnArguments(length, fanSectionData.increment),
          },
        },
        getOptions(),
      );
      fan.model = updatedSection;
    } catch (e: any) {
      if (e.statusText !== 'abort') {
        throw e;
      }
    }
  }
  onProgress?.(DEFAULT_COEFFICIENTS.update_section); // update_section coefficient
  return {
    tunnelFan: createdFan,
  };
}
